import { RingBadge } from "@/components/ui/RingBadge";
import { Badge } from "@/components/ui/Badge";
import { PillButton } from "@/components/ui/PillButton";
import { RETIRED_RINGS } from "@/lib/rings";
import type { Ring, Tool, ToolRadar } from "@/api/types";

// A tool's Tech Radar placement, the Trelica card's counterpart: where its ring — the
// blip's Jira status — and the active/deprecated status a Hold or Deprecated ring implies
// came from. Read from the tool itself (the last radar sync
// wrote it), so unlike Trelica nothing is fetched on open.

const HEADING = "mb-1 text-xs font-semibold tracking-wide text-text/60 uppercase";

/** The radar part of a tool's detail pane: its blip key, ring and quadrant, what that ring
 * means for its status, feature-level
 * rings, links to the Jira issue and Confluence write-up, and when it was last synced. A
 * tool not on the radar says so — its status was set by a person or the Trelica import. */
export function RadarSection({
  tool: t,
  noun = "tool",
  category,
  onSearch,
}: {
  /** A tool, or a practice (which has no features). */
  tool: { ring: Ring | null; radar: ToolRadar | null; radar_url: string | null; features?: Tool["features"] };
  /** What to call the entry in the text — "tool" or "practice". */
  noun?: string;
  /** The entry's category, when it has one: a quadrant pill that repeats it is left out. */
  category?: string | null;
  /** Set when the page has a search: the ring and quadrant pills then search for every entry on that ring or in that quadrant. */
  onSearch?: (field: string, value: string, toggle?: boolean) => void;
}) {
  if (!t.radar) {
    return (
      <section>
        <h4 className={HEADING}>Tech Radar</h4>
        <p className="text-sm text-text/60">Not on the Tech Radar — its status wasn't set by a radar sync.</p>
      </section>
    );
  }
  const r = t.radar;
  return (
    <section>
      <h4 className={HEADING}>Tech Radar</h4>
      <div className="flex flex-col gap-3 rounded border border-line p-3 text-sm">
        <div className="flex items-center gap-2">
          <a href={r.jira_url} target="_blank" rel="noreferrer" className="font-mono text-xs text-accent underline">
            {r.key}
          </a>
          <PillButton
            onClick={onSearch && ((toggle) => onSearch("ring", t.ring ?? "unrated", toggle))}
            title={`Show every entry rated ${t.ring ?? "unrated"}`}
          >
            <RingBadge ring={t.ring} />
          </PillButton>
          {r.quadrant &&
            r.quadrant !== category &&
            (onSearch ? (
              <PillButton
                onClick={(toggle) => onSearch("quadrant", r.quadrant!, toggle)}
                title={`Show every entry in ${r.quadrant}`}
              >
                <Badge>{r.quadrant}</Badge>
              </PillButton>
            ) : (
              <span className="text-xs text-text/60">{r.quadrant}</span>
            ))}
        </div>

        <p className="text-text/70">
          {RETIRED_RINGS.includes(t.ring)
            ? `A ${t.ring} ring marks this ${noun} deprecated — it's never suggested.`
            : t.ring
              ? `Its ${t.ring} ring keeps it active.`
              : "Unrated on the radar, so it stays active."}
        </p>

        {(t.features ?? []).length > 0 && (
          <div>
            <p className="mb-1 text-xs text-text/50">Features rated separately</p>
            <ul className="flex flex-col gap-1">
              {(t.features ?? []).map((f) => (
                <li key={f.radar_key} className="flex items-center gap-2">
                  <RingBadge ring={f.ring} />
                  {f.radar_url ? (
                    <a href={f.radar_url} target="_blank" rel="noreferrer" className="text-accent underline">
                      {f.name}
                    </a>
                  ) : (
                    f.name
                  )}
                  <span className="font-mono text-xs text-text/50">{f.radar_key}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <a href={r.jira_url} target="_blank" rel="noreferrer" className="text-accent underline">
            View in Jira
          </a>
          {t.radar_url && (
            <a href={t.radar_url} target="_blank" rel="noreferrer" className="text-accent underline">
              Radar write-up
            </a>
          )}
        </div>
        {r.synced_at && (
          <p className="text-xs text-text/50">Synced from the radar {new Date(r.synced_at).toLocaleDateString()}</p>
        )}
      </div>
    </section>
  );
}
